'use client';

import { useEffect, useState, useContext, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import { EllipsisVerticalIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import OnStationConfig from './Configurations/OnStationConfig';

export default function Integrations() {
  const { user } = useContext(AuthContext);
  const [organization, setOrganization] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [apiKey, setApiKey] = useState('');

  const viewMenuRef = useRef(null);

  // Utility function to normalize integration names
  const normalizeIntegrationName = (name) => {
    return name?.toLowerCase() || '';
  };

  useEffect(() => {
    const fetchOrganizationAndIntegrations = async () => {
      if (!user) return;

      try {
        const { data: organizationData, error: orgError } = await supabase
          .from('organization_members')
          .select('organizations(id, name)')
          .eq('user_id', user.id)
          .single();

        if (orgError) {
          console.error('Error fetching organization:', orgError);
          setError('Failed to fetch organization.');
          return;
        }

        const organization = organizationData?.organizations;
        setOrganization(organization);

        if (!organization?.id) {
          setError('No organization found.');
          return;
        }

        const { data: integrationsData, error: integrationsError } = await supabase
          .from('organization_integrations')
          .select('*')
          .eq('organization_id', organization.id);

        if (integrationsError) throw integrationsError;

        setIntegrations(integrationsData);

        const { data: metadataData, error: metadataError } = await supabase
          .from('integration_metadata')
          .select('*')
          .eq('status', 'Official');

        if (metadataError) throw metadataError;

        setMetadata(metadataData);
      } catch (err) {
        console.error('Error fetching integrations:', err);
        setError('An error occurred while fetching integrations.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationAndIntegrations();
  }, [user]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'official':
        return 'text-green-600';
      case 'coming soon':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  const findIntegrationMetadata = (integrationName) =>
    metadata.find((meta) => 
      normalizeIntegrationName(meta.name) === normalizeIntegrationName(integrationName)
    );

  const handleMenuClick = (id) => {
    setMenuOpenId((prevId) => (prevId === id ? null : id));
  };

  const handleAction = (action, integration) => {
    if (action === 'view') {
      const meta = findIntegrationMetadata(integration.integration_name);
      setSelectedIntegration({
        ...meta, // Metadata (name, description, logo_url, etc.)
        id: integration.id, // Ensure id is preserved
        api_key: integration.api_key, // Ensure api_key is preserved
      });
      setApiKey(integration.api_key || '');
      setViewModalOpen(true);
    }
    setMenuOpenId(null);
  };

  const isInstalled = (integrationName) =>
    integrations.some(
      (integration) =>
        normalizeIntegrationName(integration.integration_name) === normalizeIntegrationName(integrationName)
    );

  const handleAddIntegration = (meta) => {
    // Set selected integration with explicit null ID for new integrations
    setSelectedIntegration({
      ...meta,
      id: null // Explicitly set ID to null for new integrations
    });
    setApiKey('');
    
    // All integrations go through the same modal now
    setViewModalOpen(true);
  };

  const handleSaveIntegration = async () => {
    console.log('handleSaveIntegration called with:', {
      apiKey: apiKey,
      selectedIntegration: selectedIntegration,
      organization: organization
    });

    if (!apiKey || !selectedIntegration || !organization) {
      const missingFields = [];
      if (!apiKey) missingFields.push('API Key');
      if (!selectedIntegration) missingFields.push('Integration');
      if (!organization) missingFields.push('Organization');
      
      alert(`Please provide all required information. Missing: ${missingFields.join(', ')}`);
      return;
    }

    try {
      console.log('Attempting to insert new integration:', {
        organization_id: organization.id,
        integration_name: selectedIntegration.name,
        api_key: apiKey
      });

      const { data, error } = await supabase
        .from('organization_integrations')
        .insert({
          organization_id: organization.id,
          integration_name: selectedIntegration.name,
          api_key: apiKey,
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding integration:', error);
        alert('Failed to add integration: ' + error.message);
        return;
      }

      console.log('Successfully inserted integration:', data);
      setIntegrations((prev) => [
        ...prev,
        {
          id: data.id,
          organization_id: organization.id,
          integration_name: selectedIntegration.name,
          api_key: apiKey,
        },
      ]);

      alert('Integration added successfully!');
      setAddModalOpen(false);
      setViewModalOpen(false);
      setApiKey('');
      setSelectedIntegration(null);
    } catch (err) {
      console.error('Unexpected error adding integration:', err);
      alert('An unexpected error occurred: ' + err.message);
    }
  };

  const handleRemoveIntegration = async (integrationId) => {
    try {
      const { error } = await supabase
        .from('organization_integrations')
        .delete()
        .eq('id', integrationId);
      if (error) throw error;

      setIntegrations((prev) => prev.filter((int) => int.id !== integrationId));
      setViewModalOpen(false);
      setSelectedIntegration(null);
      setApiKey('');
      alert('Integration removed successfully!');
    } catch (err) {
      console.error('Error removing integration:', err);
      alert('Failed to remove integration: ' + err.message);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(event.target)) {
        setMenuOpenId(null);
      }
    };

    if (menuOpenId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    };

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpenId]);

  if (loading) return <div>Loading integrations...</div>;

  return (
    <div>
      {!loading && !organization ? (
        <div className="border-l-4 border-red-400 bg-red-50 p-4 mt-4 max-w-7xl mx-auto">
          <div className="flex">
            <div className="shrink-0">
              <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                You are not a member of a Company.{' '}
                <a href="/settings/Team" className="font-medium text-red-700 underline hover:text-red-600">
                  Create or join an existing one here.
                </a>
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {integrations.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Installed Integrations</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.map((integration) => {
              const meta = findIntegrationMetadata(integration.integration_name);
              return (
                <div
                  key={integration.id}
                  className="relative p-4 bg-white rounded-lg shadow border hover:shadow-lg transition-shadow"
                >
                  <div className="flex space-x-4">
                    {meta?.logo_url && (
                      <img
                        src={meta.logo_url}
                        alt={`${integration.integration_name} Logo`}
                        className="w-14 h-14 object-contain border border-gray-200 rounded-lg p-2 bg-gray-50"
                      />
                    )}
                    <div>
                      <h3 className="text-md font-semibold text-gray-800 capitalize">
                        {integration.integration_name}
                      </h3>
                      <p className="text-xs text-gray-600">{meta?.description || 'No description available.'}</p>
                      <span
                        className={`inline-block mt-2 px-2 py-1 bg-gray-100 text-xs font-medium rounded-full border ${getStatusColor(
                          meta?.status
                        )}`}
                      >
                        {meta?.status || 'Unknown'}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <button
                        className="p-1 rounded-full hover:bg-gray-100"
                        onClick={() => handleMenuClick(integration.id)}
                      >
                        <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
                      </button>
                      {menuOpenId === integration.id && (
                        <div
                          className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg border z-10"
                          ref={viewMenuRef}
                        >
                          <button
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => handleAction('view', integration)}
                          >
                            View
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {organization && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Integrations</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {metadata.map((meta) => {
              const installed = isInstalled(meta.name);
              return (
                <div
                  key={meta.id}
                  className={`p-4 bg-white rounded-xl shadow-sm border border-gray-200 transition-all ${
                    installed ? 'border-green-400' : ''
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {meta.logo_url && (
                      <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full">
                        <img
                          src={meta.logo_url}
                          alt={`${meta.name} Logo`}
                          className="w-14 h-14 object-contain border border-gray-200 rounded-lg p-2 bg-gray-50"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-md font-semibold text-gray-800 capitalize">{meta.name}</h3>
                      <p className="text-sm text-gray-600">{meta.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        meta.status?.toLowerCase() === 'official'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {meta.status}
                    </span>
                    {!installed && (
                      <button
                        className="px-4 py-1 text-sm font-medium bg-yellow-600 text-white rounded-md hover:bg-yellow-500 transition-all"
                        onClick={() => handleAddIntegration(meta)}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewModalOpen && selectedIntegration && (
        <OnStationConfig
          isOpen={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedIntegration(null);
            setApiKey('');
          }}
          onSave={async (integrationId) => {
            console.log('onSave called with integrationId:', integrationId, 'apiKey:', apiKey);
            
            if (integrationId) {
              // This is an update to an existing integration
              try {
                console.log('Updating existing integration with ID:', integrationId);
                
                const { error } = await supabase
                  .from('organization_integrations')
                  .update({ api_key: apiKey })
                  .eq('id', integrationId);
                
                if (error) {
                  console.error('Update error:', error);
                  throw error;
                }

                // Success - update local state
                setIntegrations((prev) =>
                  prev.map((int) =>
                    int.id === integrationId
                      ? { ...int, api_key: apiKey }
                      : int
                  )
                );
                setViewModalOpen(false);
                setSelectedIntegration(null);
                setApiKey('');
                alert('API key updated successfully!');
              } catch (err) {
                console.error('Error updating API key:', err);
                alert('Failed to update API key: ' + err.message);
              }
            } else {
              // This is a new integration
              console.log('Adding new integration');
              await handleSaveIntegration();
            }
          }}
          onRemove={handleRemoveIntegration}
          apiKey={apiKey}
          setApiKey={(value) => {
            console.log('setApiKey called with:', value);
            setApiKey(value);
          }}
          integration={selectedIntegration}
          isNewIntegration={!selectedIntegration?.id}
        />
      )}

      {addModalOpen && selectedIntegration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-1/3">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">
                Add Integration: {selectedIntegration.name}
              </h2>
              <button
                className="text-gray-500 hover:text-gray-800"
                onClick={() => {
                  setAddModalOpen(false);
                  setSelectedIntegration(null);
                  setApiKey('');
                }}
              >
                ×
              </button>
            </div>
            <div className="mt-4">
              <label
                htmlFor="apiKey"
                className="block text-sm font-medium text-gray-700"
              >
                API Key
              </label>
              <input
                type="text"
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm text-gray-900"
              />
            </div>
            <div className="mt-6 flex justify-end">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-400"
                onClick={() => {
                  setAddModalOpen(false);
                  setSelectedIntegration(null);
                  setApiKey('');
                }}
              >
                Cancel
              </button>
              <button
                className="ml-3 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-500"
                onClick={handleSaveIntegration}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}